'use client';

/**
 * Global error boundary — catches errors in the root layout itself.
 *
 * Kept import-free (aside from React types via JSX transform) to minimize the
 * synthetic `/_global-error` prerender surface. WEB-555/WEB-554 retired the
 * React-18-era build wrapper once the host ran React 19; this file no longer
 * depends on that workaround.
 */
export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  if (process.env.NODE_ENV !== 'production') {
    console.error('[GlobalError]', error.digest, error.message);
  }
  // WEB-326: forward root-layout errors to New Relic if the browser agent
  // loaded. Deliberately kept in the render body (not a useEffect) to
  // preserve this file's import-free constraint documented above — adding
  // `useEffect` would pull in a React import and widen the prerender
  // surface. Optional chaining + the typeof guard make it a safe no-op
  // when the agent is absent or during SSR; it mirrors the in-body
  // `console.error` already here.
  if (typeof window !== 'undefined') {
    window.newrelic?.noticeError(error);
  }
  return (
    <html lang="en">
      <body>
        <div style={{ padding: '2rem', textAlign: 'center' }}>
          <h2>This page didn&apos;t load</h2>
          <p style={{ color: '#666', marginBottom: '1rem' }}>
            Something on this page didn&apos;t load. Try again.
          </p>
          <button type="button" onClick={() => reset()}>
            Try again
          </button>
        </div>
      </body>
    </html>
  );
}
